'use client';

import { useTranslations } from 'next-intl';
import { DocumentNavButton, DocumentReaderNav } from '@/components/ui/document-reader-nav';
import type { AgendaDocument, AgendaNavSection } from './agenda-documents';

type SectionNodeProps = {
  onSelect: (document: AgendaDocument) => void;
  openDocumentKey: string;
  section: AgendaNavSection;
};

/**
 * Nesting is conveyed by indentation alone: a sub-section's title is the same size as a top-level
 * one, so a deeply nested agenda never trails off into unreadable text.
 */
function SectionNode({ onSelect, openDocumentKey, section }: SectionNodeProps) {
  const childSectionsNode = section.children.length > 0 && (
    <ul className="mt-1 space-y-2 ps-2">
      {section.children.map((child) => (
        <SectionNode
          key={child.id}
          onSelect={onSelect}
          openDocumentKey={openDocumentKey}
          section={child}
        />
      ))}
    </ul>
  );

  return (
    <li>
      <p className="text-foreground px-2 py-1 text-sm font-semibold wrap-break-word">
        <span className="text-muted-foreground me-1">{section.number}</span>
        {section.title}
      </p>

      {section.displayChildSectionsFirst && childSectionsNode}
      {section.items.length > 0 && (
        <ul className="space-y-1 ps-2">
          {section.items.map((item) => (
            <li key={item.id}>
              <p className="text-muted-foreground px-2 py-1 text-sm wrap-break-word">
                <span className="me-1 font-medium">{item.number}</span>
                {item.title}
              </p>
              {item.documents.length > 0 && (
                <ul className="ps-2">
                  {item.documents.map((document) => (
                    <li key={document.key}>
                      <DocumentNavButton
                        isOpen={document.key === openDocumentKey}
                        onClick={() => onSelect(document)}
                        title={document.title}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {!section.displayChildSectionsFirst && childSectionsNode}
    </li>
  );
}

type AgendaDocumentNavProps = {
  /** Documents belonging to the meeting itself (agenda outline, agenda packet). */
  meetingDocuments: AgendaDocument[];
  navSections: AgendaNavSection[];
  onSelect: (document: AgendaDocument) => void;
  openDocumentKey: string;
};

/**
 * The agenda as a document index: sections, their sub-sections and items, and every document that
 * hangs off them. It replaces the agenda body while a document is open, so a reader can move
 * between documents without closing the one they are reading.
 */
export function AgendaDocumentNav({
  meetingDocuments,
  navSections,
  onSelect,
  openDocumentKey,
}: AgendaDocumentNavProps) {
  const t = useTranslations('AgendaDocumentNav');

  return (
    <DocumentReaderNav
      activeKey={openDocumentKey}
      ariaLabel={t('navAriaLabel')}
      heading={t('heading')}
    >
      {meetingDocuments.length > 0 && (
        <ul className="mb-3 border-b pb-3">
          {meetingDocuments.map((document) => (
            <li key={document.key}>
              <DocumentNavButton
                isOpen={document.key === openDocumentKey}
                onClick={() => onSelect(document)}
                title={document.title}
              />
            </li>
          ))}
        </ul>
      )}

      {navSections.length === 0 ? (
        <p className="text-muted-foreground px-2 py-1 text-sm">{t('noSections')}</p>
      ) : (
        <ul className="space-y-2">
          {navSections.map((section) => (
            <SectionNode
              key={section.id}
              onSelect={onSelect}
              openDocumentKey={openDocumentKey}
              section={section}
            />
          ))}
        </ul>
      )}
    </DocumentReaderNav>
  );
}
